import React, { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import Loader from '../../../../_common/Loader/Loader';
import classes from '../../dashboard.module.scss';
import Api from '../../../../../api/api';

const COLORS = {
  fullCoverage: '#22C55E',
  covered: '#6366F1',
  gap: '#EF4444',
};

const buildSeries = (rows) => {
  const patientMap = {};

  rows.forEach((item) => {
    if (!patientMap[item.patient]) {
      patientMap[item.patient] = { patient: item.patient, segments: [] };
    }
    item.segments.forEach((seg) => {
      const exists = patientMap[item.patient].segments.some(
        (s) => s.start === seg.start && s.end === seg.end && s.state === seg.state,
      );
      if (!exists) patientMap[item.patient].segments.push(seg);
    });
  });

  const fullCoverageData = [];
  const coveredData = [];
  const gapData = [];

  Object.values(patientMap).forEach((p) => {
    const hasGap = p.segments.some((s) => s.state === 'gap');

    p.segments.forEach((seg) => {
      const point = {
        x: p.patient,
        y: [new Date(seg.start).getTime(), new Date(seg.end).getTime()],
      };

      if (!hasGap) fullCoverageData.push(point);
      else if (seg.state === 'gap') gapData.push(point);
      else coveredData.push(point);
    });
  });

  return [
    { name: 'Full coverage', data: fullCoverageData, path: 'fullCoverage' },
    { name: 'Covered', data: coveredData, path: 'covered' },
    { name: 'Gap', data: gapData, path: 'gap' },
  ].filter((s) => s.data.length > 0);
};

const buildChartOptions = (series) => ({
  chart: {
    type: 'rangeBar',
    toolbar: { show: false },
    fontFamily: 'inherit',
  },
  plotOptions: {
    bar: {
      horizontal: true,
      barHeight: '60%',
      rangeBarGroupRows: true,
    },
  },
  colors: series.map((s) => COLORS[s.path]),
  dataLabels: { enabled: false },
  xaxis: {
    type: 'datetime',
    labels: {
      style: { colors: '#9CA3AF', fontSize: '11px' },
      datetimeFormatter: { day: 'MMM d', month: 'MMM d' },
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: {
      formatter: (val) => (typeof val === 'number' ? '' : val),
      style: { colors: '#374151', fontSize: '12px' },
      maxWidth: 200,
    },
  },
  grid: { borderColor: '#F3F4F6', strokeDashArray: 4 },
  legend: {
    show: true,
    showForSingleSeries: true,
    showForNullSeries: true,
    showForZeroSeries: true,
    position: 'bottom',
    horizontalAlign: 'center',
    offsetX: 10,
    markers: { radius: 10, width: 12, height: 12 },
    labels: { colors: '#374151' },
    fontSize: '12px',
  },
  tooltip: {
    x: { format: 'MMM d, yyyy' },
  },
});

const MedicationCoverage = ({ requestData }) => {
  const [medicationCoverageData, setMedicationCoverageData] = useState([]);
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getMedicationCoverage(requestData);

        setMedicationCoverageData(data.data || []);
        loadingToggle(false);
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [requestData]);

  const series = useMemo(() => buildSeries(medicationCoverageData), [medicationCoverageData]);

  const uniquePatientCount = useMemo(
    () => new Set(medicationCoverageData.map((r) => r.patient)).size,
    [medicationCoverageData],
  );

  const chartHeight = Math.max(300, uniquePatientCount * 52 + 100);

  return (
    <div className={classes.chart_kpi_wrapper}>
      <p style={{
        margin: 0, fontWeight: 700, fontSize: 15, color: '#111827', padding: '16px 16px 0',
      }}
      >
        Medication coverage timeline
      </p>

      {loading
        ? <Loader className={classes.chart_kpi_wrapper_loader} />

        : !medicationCoverageData.length
          ? <div className={classes.chart_is_empty}>No data to display</div>

          : (
            <div style={{ paddingLeft: 5 }}>
              <p style={{ margin: '2px 0 0 11px', fontSize: 12, color: '#9CA3AF' }}>
                Gaps in coverage directly lower PDC scores
              </p>

              <Chart
                type="rangeBar"
                height={chartHeight}
                series={series}
                options={buildChartOptions(series)}
              />
            </div>
          )}
    </div>
  );
};

export default MedicationCoverage;
